import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSEO, TAX_TYPE_SEO } from '../hooks/useSEO';
import { ArrowRight, CheckCircle, Info, Calculator, FileText, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Extended tax type information
const TAX_TYPE_INFO = {
  'omzetbelasting': {
    code: 'B / F',
    fullName: 'Omzetbelasting (BTW)',
    description: 'De omzetbelasting, beter bekend als BTW (Belasting Toegevoegde Waarde), is een belasting op de toegevoegde waarde van goederen en diensten. Ondernemers dragen deze belasting af aan de Belastingdienst.',
    codes: [
      { code: 'B', name: 'BTW Aangifte', description: 'Reguliere BTW-aangifte' },
      { code: 'F', name: 'BTW Naheffing', description: 'Naheffingsaanslag omzetbelasting' }
    ],
    periods: ['Maandelijks', 'Per kwartaal', 'Jaarlijks'],
    examples: ['4865527461501300', '2123456789150100'],
    tips: [
      'BTW-aangifte moet meestal per kwartaal worden ingediend',
      'De deadline is de laatste dag van de maand volgend op het kwartaal',
      'Kleine ondernemers kunnen in aanmerking komen voor de KOR'
    ],
    relatedLinks: [
      { title: 'Belastingdienst BTW', url: 'https://www.belastingdienst.nl/btw' },
      { title: 'BTW-aangifte indienen', url: 'https://www.belastingdienst.nl/btw_aangifte' }
    ]
  },
  'loonheffing': {
    code: 'L / A',
    fullName: 'Loonheffing',
    description: 'Loonheffing is de verzamelnaam voor loonbelasting en premies volksverzekeringen die werkgevers inhouden op het loon van werknemers en afdragen aan de Belastingdienst.',
    codes: [
      { code: 'L', name: 'Loonheffing Aangifte', description: 'Reguliere loonaangifte' },
      { code: 'A', name: 'Loonheffing Naheffing', description: 'Naheffingsaanslag loonheffing' }
    ],
    periods: ['Per 4 weken', 'Maandelijks'],
    examples: ['6123456789610100', '0123456789010100'],
    tips: [
      'Loonaangifte moet uiterlijk de laatste dag van de maand na het aangiftetijdvak worden ingediend',
      'Vergeet niet om ook de jaaropgave in te dienen',
      'Bij te laat betalen volgt een verzuimboete'
    ],
    relatedLinks: [
      { title: 'Belastingdienst Loonheffing', url: 'https://www.belastingdienst.nl/loonheffingen' }
    ]
  },
  'inkomstenbelasting': {
    code: 'H',
    fullName: 'Inkomstenbelasting',
    description: 'Inkomstenbelasting is de belasting die particulieren betalen over hun inkomen uit werk, woning, sparen en beleggen. De aanslag wordt jaarlijks vastgesteld.',
    codes: [
      { code: 'H', name: 'Inkomstenbelasting', description: 'Aanslag inkomstenbelasting' },
      { code: 'P', name: 'Conserverende aanslag', description: 'Conserverende aanslag IB' }
    ],
    periods: ['Jaarlijks'],
    examples: ['7012345678706400', '7012345678716300'],
    tips: [
      'Doe aangifte vóór 1 mei of vraag uitstel aan',
      'Check of je in aanmerking komt voor aftrekposten',
      'Voorlopige aanslagen worden maandelijks geïnd'
    ],
    relatedLinks: [
      { title: 'Aangifte inkomstenbelasting', url: 'https://www.belastingdienst.nl/aangifte' }
    ]
  },
  'vennootschapsbelasting': {
    code: 'V',
    fullName: 'Vennootschapsbelasting',
    description: 'Vennootschapsbelasting (VPB) is de belasting die rechtspersonen zoals BV\'s en NV\'s betalen over hun winst.',
    codes: [
      { code: 'V', name: 'Vennootschapsbelasting', description: 'Aanslag vennootschapsbelasting' }
    ],
    periods: ['Jaarlijks (boekjaar)'],
    examples: ['8012345674806400'],
    tips: [
      'VPB-aangifte moet binnen 5 maanden na afloop van het boekjaar worden ingediend',
      'Het tarief is 19% tot €200.000 en 25,8% daarboven (2024)',
      'Verliezen kunnen worden verrekend met winsten'
    ],
    relatedLinks: [
      { title: 'VPB-tarief', url: 'https://www.belastingdienst.nl/vpb' }
    ]
  },
  'motorrijtuigenbelasting': {
    code: 'M / Y',
    fullName: 'Motorrijtuigenbelasting',
    description: 'Motorrijtuigenbelasting (MRB), ook wel wegenbelasting genoemd, betaal je voor het hebben van een motorvoertuig. De hoogte is afhankelijk van gewicht, brandstof en provincie.',
    codes: [
      { code: 'M', name: 'MRB Aangifte', description: 'Reguliere motorrijtuigenbelasting' },
      { code: 'Y', name: 'MRB Naheffing', description: 'Naheffingsaanslag MRB' }
    ],
    periods: ['Per 3 maanden', 'Jaarlijks'],
    examples: ['7812345678786500'],
    tips: [
      'MRB wordt automatisch in rekening gebracht als je een kenteken op je naam hebt',
      'Bij verkoop moet je het kenteken direct overschrijven',
      'Elektrische voertuigen zijn vrijgesteld van MRB'
    ],
    relatedLinks: [
      { title: 'RDW Motorrijtuigenbelasting', url: 'https://www.rdw.nl' }
    ]
  },
  'toeslagen': {
    code: 'T',
    fullName: 'Toeslagen',
    description: 'Toeslagen zijn financiële tegemoetkomingen van de overheid voor huishoudens met lagere inkomens. Dit omvat zorgtoeslag, huurtoeslag, kinderopvangtoeslag en kindgebonden budget.',
    codes: [
      { code: 'T', name: 'Toeslagen terugvordering', description: 'Terugvordering van te veel ontvangen toeslag' }
    ],
    periods: ['Per toeslag/jaar'],
    examples: ['2312345678235600'],
    tips: [
      'Wijzigingen in je situatie moet je direct doorgeven',
      'Te veel ontvangen toeslag moet worden terugbetaald',
      'Je kunt een betalingsregeling aanvragen'
    ],
    relatedLinks: [
      { title: 'Toeslagen.nl', url: 'https://www.toeslagen.nl' }
    ]
  },
  'zorgverzekeringswet': {
    code: 'W',
    fullName: 'Zorgverzekeringswet',
    description: 'De inkomensafhankelijke bijdrage Zorgverzekeringswet (ZVW) wordt betaald door werkgevers voor hun werknemers en door zelfstandigen over hun winst.',
    codes: [
      { code: 'W', name: 'ZVW Bijdrage', description: 'Inkomensafhankelijke bijdrage ZVW' }
    ],
    periods: ['Jaarlijks'],
    examples: ['7512345678756400'],
    tips: [
      'De ZVW-bijdrage is onderdeel van de aanslag inkomstenbelasting voor zelfstandigen',
      'Het percentage is maximaal 5,43% over maximaal €66.956 (2024)',
      'Werkgevers dragen de bijdrage af via de loonaangifte'
    ],
    relatedLinks: [
      { title: 'ZVW Info', url: 'https://www.belastingdienst.nl/zvw' }
    ]
  }
};

export default function TaxTypePage() {
  const { taxType } = useParams();
  const [kenmerk, setKenmerk] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const seoData = TAX_TYPE_SEO[taxType];
  const taxInfo = TAX_TYPE_INFO[taxType];
  
  useSEO(seoData?.title, seoData?.description);
  
  // If tax type not found, show 404-like page
  if (!seoData || !taxInfo) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl text-center">
        <h1 className="text-3xl font-bold mb-4">Belastingtype niet gevonden</h1>
        <p className="text-muted mb-8">Dit belastingtype bestaat niet of wordt nog niet ondersteund.</p>
        <Link to="/" className="text-accent hover:underline">Terug naar home</Link>
      </div>
    );
  }
  
  const handleDecode = async () => {
    if (!kenmerk.trim()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/decode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kenmerk: kenmerk.trim() })
      });
      
      const data = await response.json();
      setResult(data);
      
      if (!data.valid) {
        toast.error(data.error || 'Ongeldig kenmerk');
      }
    } catch (error) {
      toast.error('Er ging iets mis');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      {/* Breadcrumb */}
      <nav className="text-sm text-muted mb-6">
        <Link to="/" className="hover:text-accent">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/uitleg" className="hover:text-accent">Uitleg</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{taxInfo.fullName}</span>
      </nav>
      
      {/* Hero Section */}
      <header className="mb-12">
        <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-medium mb-4">
          <FileText className="w-4 h-4" />
          Code: {taxInfo.code}
        </div>
        <h1 className="text-4xl font-bold mb-4">{seoData.h1}</h1>
        <p className="text-lg text-muted">{seoData.intro}</p>
      </header>
      
      {/* Quick Decoder */}
      <section className="bg-card border border-border rounded-2xl p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Calculator className="w-5 h-5 text-accent" />
          {taxInfo.fullName} Kenmerk Decoderen
        </h2>
        <div className="flex gap-3">
          <input
            type="text"
            value={kenmerk}
            onChange={(e) => setKenmerk(e.target.value)}
            placeholder="Voer betalingskenmerk in..."
            className="flex-1 px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent"
            onKeyDown={(e) => e.key === 'Enter' && handleDecode()}
          />
          <button
            onClick={handleDecode}
            disabled={loading}
            className="bg-accent text-white px-6 py-3 rounded-xl font-medium hover:bg-accent/90 transition-colors disabled:opacity-50"
          >
            {loading ? 'Laden...' : 'Decodeer'}
          </button>
        </div>
        
        {result && result.valid && (
          <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium mb-2">
              <CheckCircle className="w-5 h-5" />
              Geldig kenmerk
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted">Belastingtype:</span>
                <span className="ml-2 font-medium">{result.belastingtype?.nl}</span>
              </div>
              <div>
                <span className="text-muted">Jaar:</span>
                <span className="ml-2 font-medium">{result.jaar}</span>
              </div>
              <div>
                <span className="text-muted">Tijdvak:</span>
                <span className="ml-2 font-medium">{result.tijdvak}</span>
              </div>
              <div>
                <span className="text-muted">Aanslagnummer:</span>
                <span className="ml-2 font-medium font-mono">{result.aanslagnummer}</span>
              </div>
            </div>
          </div>
        )}
      </section>
      
      {/* Description */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Wat is {taxInfo.fullName}?</h2>
        <p className="text-muted leading-relaxed">{taxInfo.description}</p>
      </section>
      
      {/* Codes */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Codes in het betalingskenmerk</h2>
        <div className="grid gap-4">
          {taxInfo.codes.map((code) => (
            <div key={code.code} className="bg-card border border-border rounded-xl p-4 flex items-start gap-4">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center font-mono font-bold text-accent">
                {code.code}
              </div>
              <div>
                <h3 className="font-semibold">{code.name}</h3>
                <p className="text-sm text-muted">{code.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
      
      {/* Periods */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Aangiftetijdvakken</h2>
        <div className="flex flex-wrap gap-2">
          {taxInfo.periods.map((period) => (
            <span key={period} className="bg-muted/10 border border-border px-4 py-2 rounded-full text-sm">
              {period}
            </span>
          ))}
        </div>
      </section>
      
      {/* Examples */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Voorbeelden</h2>
        <div className="bg-slate-900 rounded-xl p-4">
          <p className="text-sm text-slate-400 mb-2">Voorbeeld betalingskenmerken voor {taxInfo.fullName}:</p>
          {taxInfo.examples.map((example) => (
            <div key={example} className="font-mono text-green-400 mb-1">
              {example.replace(/(.{4})/g, '$1 ').trim()}
            </div>
          ))}
        </div>
      </section>
      
      {/* Tips */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-accent" />
          Tips
        </h2>
        <ul className="space-y-3">
          {taxInfo.tips.map((tip, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-muted">{tip}</span>
            </li>
          ))}
        </ul>
      </section>
      
      {/* CTA */}
      <section className="bg-gradient-to-r from-accent to-blue-600 rounded-2xl p-8 text-white text-center">
        <h2 className="text-2xl font-bold mb-2">Meer kenmerken decoderen?</h2>
        <p className="mb-6 opacity-90">Gebruik KenmerkCheck voor snelle en betrouwbare decodering van alle belastingkenmerken.</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-white text-accent px-6 py-3 rounded-xl font-semibold hover:bg-white/90 transition-colors"
        >
          Naar de decoder
          <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
      
      {/* FAQ Schema for this specific tax type */}
      <section className="mt-12">
        <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-accent" />
          Veelgestelde vragen over {taxInfo.fullName}
        </h2>
        <div className="space-y-4">
          <details className="bg-card border border-border rounded-xl p-4 group">
            <summary className="font-medium cursor-pointer list-none flex justify-between items-center">
              Hoe herken ik een {taxInfo.fullName} betalingskenmerk?
              <span className="text-accent group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <p className="mt-3 text-muted text-sm">
              {taxInfo.fullName} betalingskenmerken zijn te herkennen aan de code {taxInfo.code} in het aanslagnummer. 
              Voer het kenmerk in op KenmerkCheck om direct te zien welk type belasting het betreft.
            </p>
          </details>
          <details className="bg-card border border-border rounded-xl p-4 group">
            <summary className="font-medium cursor-pointer list-none flex justify-between items-center">
              Wat als mijn betalingskenmerk ongeldig is?
              <span className="text-accent group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <p className="mt-3 text-muted text-sm">
              Als je betalingskenmerk ongeldig is, controleer dan of je alle 16 cijfers correct hebt overgenomen. 
              Neem bij twijfel contact op met de Belastingdienst of je boekhouder.
            </p>
          </details>
        </div>
      </section>
    </div>
  );
}
